import { CheckmarkIcon, LanguageIcon } from '@postidigital/posti-components'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { TransProps, useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import Localization from '../../locales'

export interface ISelectOption {
  value: string | number
  label: string

  [key: string | number]: any
}

const langOptions: ISelectOption[] = [
  { id: 'fi', label: 'Suomi', value: 'fi' },
  { id: 'en', label: 'English', value: 'en' },
  { id: 'sv', label: 'Svenska', value: 'sv' },
]

interface IProps {
  isLast?: boolean
  isInline?: boolean
}

type Props = TransProps<any> & IProps

export const LangSelectButtonComponent: React.FC<Props> = ({ isInline }) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const desktopSelectorRef = useRef<HTMLDivElement>(null)
  const currLang = Localization.CurrentLocale

  const getActiveValue = useCallback((value: string | number): ISelectOption => {
    return langOptions.find((o) => o.value === value) ?? langOptions[0]
  }, [])

  const changeLanguage = useCallback((value: string) => {
    Localization.setLocale(value)
    window.OneTrust?.changeLanguage(value)
  }, [])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (event.target instanceof Node && !desktopSelectorRef.current?.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', closeOnOutsideClick)
    return () => document.removeEventListener('mousedown', closeOnOutsideClick)
  }, [isOpen])

  if (!isInline) {
    const activeLanguage = getActiveValue(currLang)

    return (
      <DesktopLanguageSelector ref={desktopSelectorRef}>
        <DesktopLanguageTrigger
          id="topbar-locale-dropdown"
          type="button"
          aria-expanded={isOpen}
          aria-haspopup="menu"
          aria-label={t(`common.languageSelection`)}
          onClick={() => setIsOpen((open) => !open)}
        >
          <LanguageIcon aria-hidden width="1.5rem" height="1.5rem" />
          <span>{activeLanguage.label}</span>
        </DesktopLanguageTrigger>
        {isOpen && (
          <DesktopLanguageMenu role="menu">
            {langOptions.map((option) => {
              const isSelected = option.id === currLang

              return (
                <DesktopLanguageOption
                  key={option.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={isSelected}
                  $selected={isSelected}
                  onClick={() => {
                    changeLanguage(String(option.value))
                    setIsOpen(false)
                  }}
                >
                  <span>{option.label}</span>
                  {isSelected && <CheckmarkIcon aria-hidden width="1.5rem" height="1.5rem" />}
                </DesktopLanguageOption>
              )
            })}
          </DesktopLanguageMenu>
        )}
      </DesktopLanguageSelector>
    )
  }

  return (
    <InlineLanguageGroup role="group" aria-label={t(`common.languageSelection`)}>
      {langOptions.map((option: ISelectOption) => (
        <InlineLanguageButton
          key={option.id}
          type="button"
          aria-pressed={option.id === currLang}
          onClick={() => changeLanguage(String(option.value))}
        >
          {String(option.id).toUpperCase()}
        </InlineLanguageButton>
      ))}
    </InlineLanguageGroup>
  )
}

export const LangSelectButton = observer(LangSelectButtonComponent)

const DesktopLanguageSelector = styled.div`
  position: relative;
`

const DesktopLanguageTrigger = styled.button(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.spacing.space2}rem;
    align-items: center;
    padding: ${theme.spacing.space1}rem;
    color: inherit;
    font-weight: ${theme.fontWeight.bold};
    background: transparent;
    border: 0;
    border-radius: ${theme.spacing.space1}rem;
    cursor: pointer;

    &:focus {
      border: 0 !important;
      box-shadow: none !important;
      outline: none !important;
    }

    &:focus-visible {
      box-shadow: 0 0 0 2px ${theme.colorTokens.postiOrange60} !important;
    }
  `
)

const DesktopLanguageMenu = styled.div(
  ({ theme }) => css`
    position: absolute;
    top: calc(100% + ${theme.spacing.space3}rem);
    left: 0;
    z-index: 1;
    display: flex;
    width: 7.5rem;
    overflow: hidden;
    background: ${theme.colorTokens.white};
    border: 1px solid ${theme.colorTokens.postiOrange50};
    border-radius: ${theme.spacing.space4}rem;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.14), 0 10px 20px rgba(0, 0, 0, 0.1);
    flex-direction: column;
  `
)

const DesktopLanguageOption = styled.button<{ $selected: boolean }>(
  ({ $selected, theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 3.5rem;
    padding: ${theme.spacing.space4}rem ${theme.spacing.space3}rem;
    color: ${$selected ? theme.colorTokens.postiOrange70 : theme.colorTokens.gray90};
    font-weight: ${theme.fontWeight.bold};
    text-align: left;
    background: ${$selected ? theme.colorTokens.postiOrange1 : theme.colorTokens.white};
    border: 0;
    cursor: pointer;

    & > span {
      min-width: 0;
    }

    & > svg {
      width: 1.5rem;
      min-width: 1.5rem;
      height: 1.5rem;
      margin-left: ${theme.spacing.space2}rem;
      flex-shrink: 0;
    }

    &:hover,
    &:focus {
      background: ${theme.colorTokens.postiOrange1};
      border: 0 !important;
      box-shadow: none !important;
      outline: none !important;
    }
  `
)

const InlineLanguageGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.space1}rem;
`

const InlineLanguageButton = styled.button(
  ({ theme }) => css`
    padding: ${theme.spacing.space2}rem;
    color: inherit;
    font-weight: ${theme.fontWeight.bold};
    background: transparent;
    border: 0;
    cursor: pointer;

    &[aria-pressed='true'] {
      color: ${theme.colorTokens.white};
      background: ${theme.colorTokens.gray90};
      border-radius: ${theme.spacing.space2}rem;
    }
  `
)
